import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Gavel, Plus } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import { listMyCases, type MyHrCase } from '@/api/services/hrCases.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { CASE_STATUS, formatCaseDate } from '../cases/caseFormat';
import { caseTitle } from './myCaseFormat';

function nextStep(hrCase: MyHrCase): string {
  if (hrCase.canAppeal && hrCase.appealClosesAt) {
    return `You can appeal until ${formatCaseDate(hrCase.appealClosesAt)}`;
  }
  if (hrCase.hearingAt && !hrCase.outcome) {
    return `Hearing ${formatCaseDate(hrCase.hearingAt)}`;
  }
  if (hrCase.closedAt) return `Closed ${formatCaseDate(hrCase.closedAt)}`;
  return hrCase.reference;
}

function CaseRow({ hrCase }: { hrCase: MyHrCase }): React.ReactElement {
  const navigate = useNavigate();
  const status = CASE_STATUS[hrCase.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {caseTitle(hrCase)}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300">{nextStep(hrCase)}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={status?.variant ?? 'neutral'}>{status?.label ?? hrCase.status}</Badge>
        <Button
          size="sm"
          variant={hrCase.canAppeal ? 'primary' : 'secondary'}
          onClick={() => navigate(`/my-hr-cases/${hrCase._id}`)}
        >
          Open <span className="sr-only">{hrCase.reference}</span>
        </Button>
      </div>
    </li>
  );
}

/**
 * The signed-in user's own HR matters: disciplinary matters HR has told them
 * about, and grievances they raised. Nothing about anyone else's case.
 */
export function MyCasesPage(): React.ReactElement {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-hr-cases'],
    queryFn: listMyCases,
  });
  const cases = Array.isArray(data) ? data : [];

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-12" role="status">
        <Spinner />
        <span className="sr-only">Loading your HR cases</span>
      </div>
    );
  } else if (error) {
    body = (
      <p className="p-4 text-sm text-danger" role="alert">
        {getErrorMessage(error)}
      </p>
    );
  } else if (cases.length === 0) {
    body = (
      <div className="py-10 text-center">
        <Gavel className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
          Nothing here. Grievances you raise, and matters HR shares with you, appear here.
        </p>
      </div>
    );
  } else {
    body = (
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {cases.map((hrCase) => (
          <CaseRow key={hrCase._id} hrCase={hrCase} />
        ))}
      </ul>
    );
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Self Service', to: '/dashboard' }, { label: 'My HR Cases' }]}
      icon={Gavel}
      title="My HR Cases"
      description="HR matters shared with you. Only you and the people handling each matter can see it."
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => navigate('/my-hr-cases/new')}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Raise a grievance
          </Button>
        </div>
        <Card contentClassName="p-0">{body}</Card>
      </div>
    </PageShell>
  );
}
